import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.classList.toggle('dark', next === 'dark');
    localStorage.setItem('theme', next);
    setTheme(next);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  // Unscrolled, the header floats over the always-dark hero film — keep light text there in both themes.
  const navLink = isScrolled
    ? 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors duration-200'
    : 'text-gray-300 hover:text-white transition-colors duration-200';
  const menuLink = 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors duration-200';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 dark:bg-black/80 backdrop-blur-lg border-b border-black/10 dark:border-white/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <div className="text-2xl font-bold bg-gradient-to-r from-emerald-500 to-cyan-500 dark:from-emerald-400 dark:to-cyan-400 bg-clip-text text-transparent">
              Emboscale
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('pipeline')} className={navLink}>
              Pipeline
            </button>
            <button onClick={() => scrollToSection('demo')} className={navLink}>
              Demo
            </button>
            <button onClick={() => scrollToSection('capabilities')} className={navLink}>
              Capabilities
            </button>
            <button onClick={() => scrollToSection('verification')} className={navLink}>
              Verification
            </button>
            <Link to="/blog/" className={navLink}>
              Blog
            </Link>
            <button onClick={toggleTheme} aria-label="Toggle theme" className={navLink}>
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Button
              onClick={() => scrollToSection('cta')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white transition-colors duration-200"
            >
              Book a demo
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <button onClick={toggleTheme} aria-label="Toggle theme" className={navLink}>
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={navLink}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white/95 dark:bg-black/95 backdrop-blur-lg border-t border-black/10 dark:border-white/10">
          <div className="px-4 py-4 space-y-3">
            <button
              onClick={() => scrollToSection('pipeline')}
              className={`block w-full text-left py-2 ${menuLink}`}
            >
              Pipeline
            </button>
            <button
              onClick={() => scrollToSection('demo')}
              className={`block w-full text-left py-2 ${menuLink}`}
            >
              Demo
            </button>
            <button
              onClick={() => scrollToSection('capabilities')}
              className={`block w-full text-left py-2 ${menuLink}`}
            >
              Capabilities
            </button>
            <button
              onClick={() => scrollToSection('verification')}
              className={`block w-full text-left py-2 ${menuLink}`}
            >
              Verification
            </button>
            <Link
              to="/blog/"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block w-full text-left py-2 ${menuLink}`}
            >
              Blog
            </Link>
            <Button
              onClick={() => scrollToSection('cta')}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white transition-colors duration-200"
            >
              Book a demo
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
